import {LitElement, css, html} from "lit";
import {nothing} from "lit";
import {getLineBreaks, truncateHTML} from "../Utils/DOMUtils.js";

export class AuiTextCollapse extends LitElement {

	static get properties() {
		return {
			maxLines: { type: Number, attribute: "max-lines", reflect: true },
			canCollapse: { type: Boolean, attribute: false },
			isCollapsed: { type: Boolean, attribute: "collapsed", reflect: true },
			expandText: { type: String, attribute: "expand-text", reflect: true },
			collapseText: { type: String, attribute: "collapse-text", reflect: true },
		};
	}

	static get defaultMaxLines() { return 5; }
	static get defaultCollapsed() { return true; }
	static get defaultExpandText() { return "Read more"; }
	static get defaultCollapseText() { return "Read less"; }

	constructor() {
		super();
		this.expandText = this.constructor.defaultExpandText;
		this.collapseText = this.constructor.defaultCollapseText;

		this.observerConfig = { attributes: false, childList: true, subtree: true };
		this.observer = new MutationObserver(this.onDOMChanged.bind(this));

		this.lines = [];
		this.maxLines = this.constructor.defaultMaxLines;
		this.canCollapse = false;
		this.isCollapsed = this.constructor.defaultCollapsed;
	}

	connectedCallback() {
		super.connectedCallback();
		this.observer.observe(this, this.observerConfig);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.observer.disconnect();
	}

	onDOMChanged() {
		this.updateCollapse();
	}

	async updateCollapse() {
		// Reset view to the slot, otherwise we cannot detect newlines
		this.canCollapse = false;

		// Wait for update then update
		await this.updateComplete;

		this.lines = getLineBreaks(this);
		this.canCollapse = this.lines.length > this.maxLines;

		return this.updateComplete;
	}

	toggle() {
		if(this.canCollapse)
			this.isCollapsed = !this.isCollapsed;
	}

	static get styles() {
		return css`
			:host {
				display: block;
			}
			
			[hidden] { display: none; }
			
			.container {
				word-break: break-word;
				white-space: pre-line;
			}
			
			.button {
				color: var(--primary-color);
				cursor: pointer;
			}
			
			.button:hover {
				text-decoration: underline;
			}
		`;
	}

	render() {
		return html`
			${this.canCollapse && this.isCollapsed ? html`<div class="container">${this.renderCollapse()}</div>` : nothing}
			<div class="container" ?hidden=${this.canCollapse && this.isCollapsed}><slot></slot></div>
			${this.canCollapse && !this.isCollapsed ? this.renderCollapseButton() : nothing}
		`;
	}

	renderCollapse() {
		// truncate to the amount of characters from maxLines
		let collapseChars = this.lines.slice(0, this.maxLines).reduce((totalHeight, line) => totalHeight + line.text.length, 0);

		// Adds room for one space between ellipsis and button
		collapseChars -= 1;

		// Adds room for the expand button plus some extra padding to ensure button to be always in same line
		collapseChars -= this.expandText.length + 5;

		return html`${truncateHTML(this, collapseChars)} ${this.renderExpandButton()}`;
	}

	renderExpandButton() {
		return html`<span class="button" @click=${() => this.isCollapsed = false}>${this.expandText}</span>`;
	}

	renderCollapseButton() {
		return html`<span class="button" @click=${() => this.isCollapsed = true}>${this.collapseText}</span>`;
	}

}

export default AuiTextCollapse;

customElements.define("aui-text-collapse", AuiTextCollapse);